import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { HomeStatistics, StatisticExit, PAST_STAYS_PANEL_NAME } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { Translation } from 'src/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { STAYS_PANEL_NAME } from 'src/features/home/components/WidgetEntries';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import StaySidePanel from 'src/features/visitor/components/StaySidePanel';

type Props = {
    exits?: HomeStatistics['exits'];
    isError?: boolean;
};
export default function WidgetExits({ exits, isError }: Props) {
    const { formatMessage, locale } = useIntl();
    const permissionAccessVisitor = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const translate = React.useCallback(
        (translations?: Translation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();
    const { openPanel, closePanel } = useSidePanel();

    const singleRowSelection = React.useCallback(
        (rowId: string) => setRowSelectionState({ [rowId]: true }),
        [setRowSelectionState],
    );

    const columns: ColumnDef<StatisticExit>[] = [
        {
            accessorKey: 'visitor',
            header: formatMessage(homeMessages.visitor),
            cell: (info) => {
                const entry = info.row.original;
                const visitor = entry.visitor;

                const handleClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    clearSelection();
                    closePanel(STAYS_PANEL_NAME);
                    singleRowSelection(entry?.active_stay?.id || entry?.past_stay?.id || '');
                    openPanel(PAST_STAYS_PANEL_NAME);
                };

                return (
                    <div
                        onClick={permissionAccessVisitor ? handleClick : undefined}
                        className={cn(
                            permissionAccessVisitor &&
                                'text-primary-700 hover:text-primary-600 cursor-pointer font-semibold',
                        )}
                    >
                        {visitor?.main_identifier?.value}
                    </div>
                );
            },
        },
        {
            accessorKey: 'passed_on',
            header: formatMessage(homeMessages.passedOn),
            cell: (info) =>
                new Date(info.getValue() as string).toLocaleTimeString(locale, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                }),
        },
        {
            accessorKey: 'exit_gateway',
            header: formatMessage(homeMessages.identifier),
            cell: (info) => {
                const entryGateway = info.getValue() as StatisticExit['exit_gateway'];

                return translate(entryGateway.translations);
            },
        },
    ];

    const selectedStayId = React.useMemo(() => Object.keys(rowSelectionState)[0], [rowSelectionState]);

    return (
        <GeneralBlock className="flex-1 gap-6">
            <div className="flex gap-6">
                <div className="flex size-14 items-center justify-center rounded-full border-8 border-red-50 bg-red-100 p-1 text-2xl text-red-700">
                    <FontAwesomeIcon icon={ICON_MAP.TREND_DOWN} />
                </div>
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-gray-600">
                        {formatMessage(homeMessages.totalExitsToday)}
                    </span>
                    <span className="text-lg font-bold">{isError ? '-' : exits?.total_exits}</span>
                </div>
            </div>

            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                <>
                    <StatisticTable
                        title={formatMessage(homeMessages.lastTenExits)}
                        data={exits?.last_exits}
                        columns={columns as any}
                    />
                    <SidePanel onClose={clearSelection} name={PAST_STAYS_PANEL_NAME}>
                        <StaySidePanel isActiveStay={false} selectedStayId={selectedStayId} />
                    </SidePanel>
                </>
            )}
        </GeneralBlock>
    );
}
